import '../styles.css';
import s from './CreateChat.module.css'
import {CloseIcon} from "@/utils/icons";
import CustomRadioElem from "@common/Chat/components/CustomRadioElem/CustomRadioElem";
import React, {useEffect, useMemo, useState} from "react";
import {Input, Spin} from "antd";
import classNames from "classnames";
import {useInputNew} from "@UI/InputNew/useInputNew";
import {chatApi} from "@/api";
import Participant from "@common/Chat/components/Participant";
import * as InputNew from "@UI/InputNew/Input";
import '../styles.css';
import {useAddAlert} from "@/hooks/useAddAlert";
import {useSelector} from "react-redux";
import {selectProfile} from "@store/profile.reducer";

const getCreatedChatId = (response) => {
    const responseData = response?.data;
    const nestedData = responseData?.data;
    const primitiveId = [responseData, nestedData].find((value) => (
        typeof value === 'number'
        || (typeof value === 'string' && /^\d+$/.test(value))
    ));

    if (primitiveId !== undefined) return primitiveId;

    return responseData?.id
        ?? responseData?.chat_id
        ?? responseData?.chat?.id
        ?? nestedData?.id
        ?? nestedData?.chat_id
        ?? nestedData?.chat?.id
        ?? responseData?.result?.id
        ?? responseData?.result?.chat_id;
};

const findCreatedChatId = async ({title, participants, creatorId}) => {
    const response = await chatApi.getChatList({
        'per-page': 20,
        page: 1,
        type: 'group'
    });
    const chats = Array.isArray(response?.data?.data) ? response.data.data : [];
    const participantIds = participants
        .map(({id}) => Number(id))
        .filter(Number.isFinite);
    const normalizedTitle = title?.trim();

    const createdChat = chats.find((item) => {
        const itemTitle = String(item?.title ?? item?.name ?? '').trim();
        const members = Array.isArray(item?.members) ? item.members : [];
        const memberIds = members
            .map((member) => Number(member?.user_id ?? member?.id))
            .filter((id) => Number.isFinite(id) && id !== Number(creatorId));
        const hasExpectedMembers = !memberIds.length
            || (
                memberIds.length === participantIds.length
                && participantIds.every((id) => memberIds.includes(id))
            );

        return itemTitle === normalizedTitle && hasExpectedMembers;
    });

    return createdChat?.id ?? createdChat?.chat_id;
};

const CreateChat = ({setPage, chat, typeAction, isFullPage = false}) => {
    const [participants, setParticipants] = useState([])
    const addAlert = useAddAlert();
    const [users, setUsers] = useState([])
    const chatTitle = chat?.title ? chat.title : null;
    const [title, setTitle] = useState(chatTitle);
    const [file, setFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isFocus, setIsFocus] = useState(false);
    const profile = useSelector(selectProfile);
    const [params, setParams] = useState({
        search: '',
        count: 20,
        active: 1
    })
    const getUsers = () => {
        chatApi.getUsersChatsList(params)
            .then(response => {
               if (response?.data) {
                    setUsers(
                        response?.data?.filter(user => !chat?.members?.some(chatUser => chatUser?.user_id == user?.id)).slice(0,10)
                    )
               }
            })
        ;
    }

    useEffect(() => {
        getUsers()
    }, [params]);
    const edit = (key, val) => setFile(val);
    const [bindFile] = useInputNew({
        name: "file",
        value: file,
        onChange: (e) => edit("file", e),
        placeholder: "",
        typeValid: "img",
    });

    const save = async (e) => {
        e.preventDefault()
        if (isSaving) return;

        const usersForCreate = participants.filter((participant) => (
            Number(participant?.id) !== Number(profile?.id)
        ));

        if (typeAction === 'create') {
            setIsSaving(true);

            try {
                const res = await chatApi.createChat({
                    name: title,
                    users: usersForCreate,
                    is_group: true
                });
                // Интерцептор на 4xx/5xx резолвит промис объектом ошибки axios —
                // пробрасываем его, иначе текст ошибки бэкенда теряется.
                if (res?.response) {
                    throw res;
                }
                let createdChatId = getCreatedChatId(res);

                if (!createdChatId) {
                    try {
                        createdChatId = await findCreatedChatId({
                            title,
                            participants: usersForCreate,
                            creatorId: profile?.id
                        });
                    } catch (error) {
                        createdChatId = null;
                    }
                }

                if (!createdChatId) {
                    throw new Error('Сервер подтвердил запрос, но созданный чат не найден');
                }

                setPage('inChat', createdChatId);

                const skippedUsers = Array.isArray(res?.data?.skipped_users) ? res.data.skipped_users : [];
                if (skippedUsers.length) {
                    addAlert({
                        type: 'warning',
                        title: 'Чат создан, но не все участники добавлены',
                        text: `Удалены или неактивны: ${skippedUsers.map(({id, name}) => (name ? `${name} (id ${id})` : `id ${id}`)).join(', ')}`
                    });
                }

                if (file) {
                    const formData = new FormData();
                    formData.append('file', file);
                    chatApi.updateChatImage(createdChatId, formData).catch(() => {
                        addAlert({
                            type: 'error',
                            title: 'Чат создан, но изображение загрузить не удалось'
                        });
                    });
                }
            } catch (error) {
                addAlert({
                    type: 'error',
                    title: 'Не удалось создать чат',
                    text: error?.response?.data?.error ?? error?.response?.data?.message ?? error?.message
                });
            } finally {
                setIsSaving(false);
            }
        }
        if (typeAction === 'add') {
            setIsSaving(true);

            try {
                await chatApi.addMember({
                    chatId: chat.id,
                    users: participants,
                });
                setPage('info', chat.id);
            } catch (error) {
                addAlert({
                    type: 'error',
                    title: 'Не удалось добавить участников',
                    text: error?.response?.data?.error ?? error?.response?.data?.message
                });
            } finally {
                setIsSaving(false);
            }
        }
    }

    const isSubmitBtnActive = () => {
        if (typeAction === 'create') {
            return title?.trim()?.length > 0 && !isSaving
        } else if (typeAction === 'add') {
            return participants?.length >= 1 && !isSaving
        }
    }

    const orderedUsers = useMemo(() => {
        const selectedUserIds = new Set(participants.map(({id}) => String(id)));

        return users
            .map((user, index) => ({
                user,
                index,
                isSelected: selectedUserIds.has(String(user.id)),
            }))
            .sort((first, second) => (
                Number(second.isSelected) - Number(first.isSelected)
                || first.index - second.index
            ))
            .map(({user}) => user);
    }, [participants, users]);

    return (
        <div className={classNames(s.page, 'create-chat')}>
            <div className={s.header}>
                <span className={s.title}>{chat !== null ? 'Добавление новых участников' : 'Создание чата'}</span>
                <button
                    type="button"
                    className={s.close}
                    title={isFullPage ? 'Закрыть' : 'Свернуть чат'}
                    aria-label={isFullPage ? 'Закрыть' : 'Свернуть чат'}
                    onClick={() => setPage(isFullPage ? 'main' : 'default')}
                >
                    <CloseIcon/>
                </button>
            </div>
            <form className={classNames(s.pageForm)}>
                <div className={classNames(s.multipleSelect, 'new-design-scroll')}>
                    {participants.map((participant) => (
                        <Participant participant={participant} setParticipants={setParticipants} participants={participants} key={participant.id}/>
                    ))}
                    {(!isFocus && participants?.length === 0)  && <label htmlFor={'search'} className={s.label}>Введите ФИО </label>}
                    <input
                        id={'search'}
                        className={s.inputSearch}
                        value={params.search}
                        onChange={(e) => setParams({...params, search: e.target.value})}
                        onFocus={()=>setIsFocus(true)}
                        onBlur={() => setIsFocus(false)}
                    />
                </div>
                <div className={classNames(s.users, 'new-design-scroll')}>
                    {orderedUsers.map(user => (
                        <div className={s.user} key={user.id}>
                            <CustomRadioElem
                                user={user}
                                addUsers={setParticipants}
                                participants={participants}
                                setParams={setParams}
                            />
                        </div>
                    ))}
                </div>
                <div className={s.footer}>
                    {chat == null && <div className={s.inputWr}>
                        <InputNew.InputFileNewDesign
                            className={s.inputFile}
                            {...bindFile}
                            disabled={typeAction === 'add'}
                            newDesign
                            placeholder=''
                            icon='picture'
                        />
                        <div className={s.title}>
                            <Input
                                disabled={typeAction === 'add'}
                                className={s.input}
                                value={title}
                                placeholder={'Введите название чата'}
                                onChange={e => setTitle(e.target.value)}
                            />
                        </div>
                    </div>}

                    <button type='submit'
                            className={classNames(chat !== null ? s.center : '', s.submitBtn)}
                            onClick={save}
                            disabled={!isSubmitBtnActive()}
                    >
                        {isSaving
                            ? <Spin size="small" />
                            : chat !== null
                                ? 'Обновить данные'
                                : 'Создать чат'}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default CreateChat;
